// ==============================|| CLIENT IDENTIFICATION (CIF) TYPES ||============================== //

import { PartyType } from './contacts';

export type AssessmentStatus = 'DRAFT' | 'APPROVED' | 'SUPERSEDED';
export type AssessmentStatusShown = 'DRAFT' | 'RECEIVED' | 'SUPERSEDED';
export type ClientOwnerAnswer = 'HAS_OWNERS' | 'NO_OWNERS';
export type PepAnswer = 'YES' | 'NO';
export type RepresentationGrounds = 'POSITION' | 'STATUTORY' | 'POWER_OF_ATTORNEY' | 'OTHER';
export type AssessmentHistoryType = 'RECEIVED' | 'RECEIPT_REVERTED';
export type TierCode = 'SIMPLIFIED' | 'STANDARD' | 'ENHANCED';
export type MissingItemBlock = 'CLIENT_DETAILS' | 'RELATED_PARTIES' | 'IDENTIFICATION';

/** The tiers a client identification is started at, in their order; `OUTSIDE_AML_ACT` is a level but never a tier. */
export const TIER_CODES: TierCode[] = ['SIMPLIFIED', 'STANDARD', 'ENHANCED'];

export const REPRESENTATION_GROUNDS: RepresentationGrounds[] = ['POSITION', 'STATUTORY', 'POWER_OF_ATTORNEY', 'OTHER'];

export interface IdentityAddress {
    address1: string | null;
    address2: string | null;
    city: string | null;
    zip: string | null;
    countryCode: string | null;
}

export interface IdentityDocument {
    number: string | null;
    issuingCountryCode: string | null;
    expiryDate: string | null;
}

/** One shape for a person and a company; the keys the other type does not use stay null. */
export interface IdentityFields {
    firstName: string | null;
    lastName: string | null;
    name: string | null;
    officialName: string | null;
    legalForm: string | null;
    personalCode: string | null;
    regCode: string | null;
    dateOfBirth: string | null;
    placeOfBirthCountryCode: string | null;
    placeOfBirthCity: string | null;
    nationalities: string[];
    identificationAddress: IdentityAddress;
    visitingAddress: IdentityAddress;
    identityDocument: IdentityDocument;
    occupation: string | null;
    employmentStatusCode: string | null;
    natureOfBusiness: string | null;
    registrationDate: string | null;
    registrationAuthority: string | null;
}

export interface AssessmentHistoryDto {
    type: AssessmentHistoryType;
    employeeName: string | null;
    occurredAt: string;
    comment: string | null;
}

export interface ClientIdentificationDto {
    kind: 'CLIENT_IDENTIFICATION';
    status: AssessmentStatus;
    statusShown: AssessmentStatusShown;
    tierCode: TierCode;
    clientOwnerAnswer: ClientOwnerAnswer | null;
    pepAnswer: PepAnswer | null;
    pepDetails: string | null;
    sourceOfFundsCodes: string[];
    sourceOfFundsNote: string | null;
    relevantBanks: string | null;
    clientConfirmedOn: string | null;
    clientConfirmationLink: string | null;
    identifiedByEmployeeId: number | null;
    identifiedByName: string | null;
    identifiedOn: string | null;
    responsibleEmployeeId: number | null;
    responsibleName: string | null;
    receivedOn: string | null;
    receivedByName: string | null;
    supersedesContractId: number | null;
    supersededByContractId: number | null;
    isNewest: boolean;
    isLocked: boolean;
    mayEdit: boolean;
    mayDelete: boolean;
    mayReceive: boolean;
    mayRevert: boolean;
    mayStartNewVersion: boolean;
    history: AssessmentHistoryDto[];
}

export interface ClientIdentificationListItemDto {
    contractId: number;
    contractName: string;
    status: AssessmentStatus;
    statusShown: AssessmentStatusShown;
    tierCode: TierCode;
    identifiedByName: string | null;
    identifiedOn: string | null;
    responsibleName: string | null;
    receivedOn: string | null;
    receivedByName: string | null;
    isNewest: boolean;
    supersedesContractId: number | null;
    supersededByContractId: number | null;
}

export interface ClientIdentificationListDto {
    /** The user may start one, the party is a person or a company, and none is in progress. */
    canStart: boolean;
    /** Newest first. */
    items: ClientIdentificationListItemDto[];
}

export interface RelatedPartyRowDto {
    id: number;
    roleCode: string;
    linkedPartyId: number | null;
    linkedPartyName: string | null;
    linkedPartyType: PartyType | null;
    /** A typed name, where the row holds no contact. */
    name: string | null;
    note: string | null;
    actingRoleCode: string | null;
    representationGroundsCode: RepresentationGrounds | null;
    representationGroundsNote: string | null;
}

export interface RelatedPartyGroupDto {
    roleCode: string;
    name: string;
    namePlural: string;
    kind: 'LIST' | 'OWNER' | 'ENTITY';
    clientTypes: PartyType[];
    isMandatory: boolean;
    /** The role asks more than a name, which only a contact can hold. */
    isContactsOnly: boolean;
    takesGrounds: boolean;
    takesActingRole: boolean;
    itemKeys: string[];
    rows: RelatedPartyRowDto[];
}

export interface NotAskedRoleDto {
    roleCode: string;
    name: string;
    namePlural: string;
    rows: RelatedPartyRowDto[];
}

export interface RelatedPartiesDto {
    /** The roles the tier asks for the client's type, in the dictionary's order. */
    groups: RelatedPartyGroupDto[];
    /** Stored people in a role the tier no longer asks. */
    notAskedAtTier: NotAskedRoleDto[];
}

export interface RelatedPartyRowUpdateDto {
    /** Absent on a new row. */
    id?: number;
    roleCode: string;
    linkedPartyId: number | null;
    name: string | null;
    note: string | null;
    actingRoleCode: string | null;
    representationGroundsCode: RepresentationGrounds | null;
    representationGroundsNote: string | null;
}

export interface AssessmentUpdateDto {
    tierCode: TierCode;
    clientOwnerAnswer?: ClientOwnerAnswer | null;
    pepAnswer?: PepAnswer | null;
    pepDetails?: string | null;
    sourceOfFundsCodes?: string[];
    sourceOfFundsNote?: string | null;
    relevantBanks?: string | null;
}

export interface IdentityUpdateDto {
    partyId: number;
    fields: IdentityFields;
}

export interface ReceiveDto {
    receivedOn: string;
    clientConfirmedOn: string | null;
    clientConfirmationLink: string | null;
}

export interface RevertReceiptDto {
    comment: string | null;
}

export interface ReassessDto {
    tierCode: TierCode | null;
}

export interface PanelItemDto {
    key: string;
    isMissing: boolean;
}

export interface PanelGroundsDto {
    code: RepresentationGrounds | null;
    note: string | null;
    isMissing: boolean;
}

export interface PanelPersonDto {
    partyId: number;
    partyType: PartyType;
    name: string;
    roleCodes: string[];
    roleNames: string[];
    /** The union of what the person's roles ask. */
    itemKeys: string[];
    fields: IdentityFields;
    /** `null` for a person in no acts-for-client role. */
    grounds: PanelGroundsDto | null;
    rowIds: number[];
}

export interface PanelTypedRowDto {
    rowId: number;
    roleCode: string;
    roleName: string;
    name: string;
    grounds: PanelGroundsDto;
}

export interface MissingMandatoryRoleDto {
    roleCode: string;
    namePlural: string;
}

export interface MissingItemDto {
    key: string;
    block: MissingItemBlock;
    partyId: number | null;
}

export interface ClientIdentificationPanelDto {
    tierCode: TierCode;
    clientPartyId: number;
    clientPartyType: PartyType;
    clientFields: IdentityFields;
    clientItems: PanelItemDto[];
    employers: string[];
    asksSourceOfFunds: boolean;
    sourceOfFunds: {
        codes: string[];
        note: string | null;
        relevantBanks: string | null;
        isMissing: boolean;
        isNoteMissing: boolean;
    };
    asksOwnerAnswer: boolean;
    ownerAnswer: { answer: ClientOwnerAnswer | null; isMissing: boolean };
    asksPep: boolean;
    pep: { answer: PepAnswer | null; details: string | null; isMissing: boolean };
    people: PanelPersonDto[];
    typedRows: PanelTypedRowDto[];
    missingMandatoryRoles: MissingMandatoryRoleDto[];
    missingItems: MissingItemDto[];
    /** `missingItems.length + missingMandatoryRoles.length`. */
    missingCount: number;
}
